// FLAG:useDocuAi — which currency does the review component show, and is the receipt bookable in it?
//
// Two questions, one source. The display needs a symbol (CurrencyInput/formatCurrency expect one
// and otherwise hard-code „€"), while booking needs the yes/no answer: bookingApi supports only
// euros, and its payload (mapExpense/mapRevenue) has no currency field at all. The actual block is
// enforced server-side in receiptReviewCaseService (registerReviewedExpenseInBooking); this module
// is its UI counterpart and deliberately mirrors the same semantics as api/src/services/_helpers/
// foreignCurrencyInvoice.ts so the display and the block cannot drift apart.
export const EURO_CODE = 'EUR'
export const EURO_SYMBOL = '€'

export interface ForeignCurrencyFacts {
  conversionRequired?: boolean | null
  originalCurrency?: string | null
}

export interface ReviewCurrency {
  // ISO 4217 code, or null if the receipt specifies no readable one
  code: string | null
  // what is shown beside amounts — never „€" when the receipt was identified as foreign currency
  symbol: string
  isForeign: boolean
}

export const normalizeCurrencyCode = (value?: string | null): string | null => {
  if (typeof value !== 'string') return null
  const normalized = value.trim().toUpperCase()
  return /^[A-Z]{3}$/.test(normalized) ? normalized : null
}

/**
 * `conversionRequired` is the stronger signal: buildForeignCurrencyInvoiceFacts sets it even when
 * only a validation code reports the foreign currency and the currency code itself was unreadable.
 * Such a receipt therefore gets NO symbol AT ALL rather than an incorrect „€".
 *
 * With no signal at all, the currency remains euro — an invoice without a currency declaration is
 * the normal case and must neither look different nor become unbookable.
 */
export const resolveReviewCurrency = (
  enabled: boolean,
  currency?: string | null,
  invoiceFacts?: ForeignCurrencyFacts | null,
): ReviewCurrency => {
  // `enabled` is the STRUCTURAL flag gate, as in findUnbookableVatFact (rationale there): flag-OFF
  // GUARANTEES euros — not merely „because no invoiceFacts exist at all". That would be a claim
  // about every writer of file metaData, which the flag guarantee must not rely on: otherwise a
  // receipt identified as USD while the flag was ON would still show „USD" beside every line-item
  // amount in the legacy form after a rollback.
  if (!enabled) return { code: null, symbol: EURO_SYMBOL, isForeign: false }
  const flaggedForeign = invoiceFacts?.conversionRequired === true
  const code = normalizeCurrencyCode(
    flaggedForeign ? (invoiceFacts?.originalCurrency ?? currency) : currency,
  )
  const isForeign = flaggedForeign || (!!code && code !== EURO_CODE)
  return {
    code,
    symbol: isForeign ? (code ?? '') : EURO_SYMBOL,
    isForeign,
  }
}
